import { useEffect, useState } from "react"
import { getUserToken } from "@/utils/userAuthenticate"; 
import instance from "@/axios";
import { useTranslation } from "react-i18next";
import "@/localisation/index"
import { BackButtonIcon, SaveButtonIcon, ImportIcon, ExportIcon } from "@/components/Icons" ;
import SendTestEmail from "@/components/EmailTemplates/SendTestEmail"
import { Splide, SplideSlide } from "@splidejs/react-splide";
import {sanitizeInput} from "@/hooks/useInputSanitization";
import { FaInfoCircle } from "react-icons/fa";
import MarkdownEditor from "./RichTextEditor";
import PhysicalTemplateModalOpener from "@/components/EmailTemplates/PhysicalTemplateModal"

export default function RenderEmailTemplate({setViewType, template}){
    const {t} = useTranslation()
    const [showTooltip, setShowTooltip] = useState(false);
    const [emailConfig, setEmailConfig] = useState({
        subject: template.emailConfig?.emailSubject || "",
        senderName: template.emailConfig?.senderName || "",
        senderEmail: template.emailConfig?.senderEmail || "",
    })
    const [bodyData, setBodyData] = useState({
        bodyMessage: "",
        errorMsg: "",
        emailError: "",
        isEmailSending: false,
        isSavingTemplate: false,
        templateBannerImage: template.updatedValues?.template_image || template.defaultValues.template_image,
        templateBrandImage: template.updatedValues?.template_brand_image ? template.defaultValues.template_brand_image : "",
        merchantMessage: template.updatedValues?.message ? template.updatedValues?.message : template.defaultValues.message || "",
    })
    const [model, setModel] = useState(false)
    const isPhysicalTemplate = template.type === "physical-send-as-gift";

    const [physicalTemplates, setPhysicalTemplates] = useState([]);

    // Fetch physical templates on component mount
    useEffect(() => {
        if (template?.physical_templates && template.physical_templates.length > 0) {
            setPhysicalTemplates(template.physical_templates);
        }
    }, [template]);

    const [templateImages, setTemplateImages] = useState(
        template.categoryTemplates || []
    );

    const [selectedPhysicalTemplate, setSelectedPhysicalTemplate] = useState(
        template.updatedValues?.template_image ||
        template.defaultValues?.template_image ||
        ""
    );

    const [selectedCover, setSelectedCover] = useState("");

    const [termsAndConditions, setTermsAndConditions] = useState(
        template.updatedValues?.terms || template.defaultValues?.terms || ''
    )

    useEffect(()=> {
        const bodyElement = document.querySelector('body')
        model ? bodyElement.classList.add('overflow-hidden') : bodyElement.classList.remove('overflow-hidden')
    }, [model])

    // Select first physical template image by default
    useEffect(() => {
        if (physicalTemplates.length > 0 && !selectedPhysicalTemplate) {
            setSelectedPhysicalTemplate(physicalTemplates[0]);
        }
    }, [physicalTemplates]);

    useEffect(() => {
        console.log("Physical Templates:", physicalTemplates);
        console.log("Selected Physical Template:", selectedPhysicalTemplate);
    }, [physicalTemplates, selectedPhysicalTemplate]);

    const saveTemplateChanges=async()=>{
        try {
            setBodyData((prev) => ({ ...prev, errorMsg: "", isSavingTemplate: true }))
            const url = "/mail-template/update";
            const headers = { Authorization: getUserToken() };
            const body = {
                templateId: template.id,
                updateObj: {
                    message: bodyData.merchantMessage,
                    template_image: isPhysicalTemplate
                        ? selectedPhysicalTemplate
                        : bodyData.templateBannerImage,
                    terms: termsAndConditions
                },
                emailConfig: {
                    emailSubject: emailConfig.subject,
                    senderName: emailConfig.senderName,
                    senderEmail: emailConfig.senderEmail
                },
                categoryTemplates: templateImages || []
            }
            const res = await instance.put(url, body, { headers });
            const {success, message} = res.data
            if(success){
                alert(t("emailTemplates.templateUpdated"))
                setViewType("TEMPLATE_LIST")
            }else {
                alert(message)
            }
        } catch (error) {
            console.log('error', error.message)
            alert(error.message)
        } finally {
            setBodyData((prev) => ({ ...prev, isSavingTemplate: false }))
        }
    }

    const BackButton=()=>(
        <div className="back-button" onClick={() => setViewType("TEMPLATE_LIST")}>
            <div className="icon-container">
                <BackButtonIcon />
            </div>
            <span className="button-text">{t("emailTemplates.back")}</span>
        </div>
    )

    const SaveButton=()=>(
        <div className="back-button" onClick={saveTemplateChanges} disabled={bodyData.isSavingTemplate}>
            <div className="icon-container">
                <SaveButtonIcon />
            </div>
            <span className="button-text">{t("emailTemplates.save")}</span>
        </div>
    )

    const handleAddImage =() => {
        const fileInput = document.getElementById("banner-file-picker");
        const file = fileInput.files[0];
        
        if (!file) {
            alert(t("createGiftcard.selectImage"));
            return;
        }
        const reader = new FileReader();

        reader.onloadend =() => {
            const img = new Image();
            img.onload =async () => {
                if (img.width === 600 && img.height === 250) {
                    const uploadedImageUrl = reader.result;
                    const brandImageEle = document.getElementById("template-banner-image");
                    brandImageEle && (brandImageEle.src = uploadedImageUrl);
                    setBodyData((prev) => ({ ...prev, templateBannerImage: uploadedImageUrl.split(",")[1] }));
                } else {
                    alert(t("createGiftcard.modelImageInvalid") + `${img.width}x${img.height}`);
                }
            };
    
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className="template-container">
            <div className="template-container__header">
                <BackButton />
                <div className="template-container__header__right">
                    <SendTestEmail templateId={template.id} type="send-as-gift" />
                    <SaveButton />
                </div>
            </div>
            <div className="template-container__body">
                <div className="product">
                    <div className="product__outline">
                        <div className="product__header">{t("emailTemplates.emailPreview")}</div>
                        <div dangerouslySetInnerHTML={{__html: template.html_content}} />
                    </div>
                </div>
                <div>
                    <div className="email-configuration">
                        <h2 className="email-configuration__heading">{t("emailTemplates.emailConfiguration")}</h2>
                        <div className="email-configuration__field">
                            <label htmlFor="email-subject" className="email-configuration__label">
                                {t("emailTemplates.emailSubject")}
                            </label>
                            <textarea 
                                rows={4}
                                id="email-subject"
                                placeholder={t("emailTemplates.enterEmailSubject")}
                                value={emailConfig.subject}
                                onChange={(e)=>setEmailConfig((prev)=>({...prev, subject: sanitizeInput(e.target.value, 'textarea')}))}
                            >
                                {emailConfig.subject}
                            </textarea>
                        </div>

                        <div className="email-configuration__field" id="sender-email">
                            <div>
                                <div className="tooltip-icon-container">
                                    <label htmlFor="sender-email" className="email-configuration__label">
                                        {t("emailTemplates.senderName")}
                                    </label>
                                    <div
                                        className="tooltip-icon"
                                        onMouseEnter={() => setShowTooltip(true)}
                                        onMouseLeave={() => setShowTooltip(false)}
                                    >
                                        <FaInfoCircle size={18} color="#888" />
                                        {showTooltip && (
                                        <span className="tooltip-text">
                                            {t("emailTemplates.tooltipInfo")}
                                        </span>
                                        )}
                                    </div>
                                </div>
                                <input
                                    type="email"
                                    id="sender-email"
                                    className="email-configuration__input"
                                    placeholder={t("emailTemplates.enterSenderName")}
                                    value={emailConfig.senderName}
                                    onChange={(e) =>
                                        setEmailConfig((prev) => ({ ...prev, senderName: sanitizeInput(e.target.value, 'email') }))
                                    }
                                />
                            </div>
                            <p className="error-msg">{bodyData.emailError}</p>
                        </div>
                        <div className="email-configuration__field" id="sender-email">
                            <div>
                                <label htmlFor="sender-email" className="email-configuration__label">
                                    {t("emailTemplates.senderEmail")}
                                </label>
                                <input
                                    type="email"
                                    id="sender-email"
                                    className="email-configuration__input"
                                    placeholder={t("emailTemplates.enterSenderEmail")}
                                    value={emailConfig.senderEmail}
                                    onChange={(e) =>
                                        setEmailConfig((prev) => ({ ...prev, senderEmail: sanitizeInput(e.target.value, 'email') }))
                                    }
                                />
                            </div>
                            <p className="error-msg">{bodyData.emailError}</p>
                        </div>
                    </div>
                    <div className="template-customisations">
                        <h4>{t("emailTemplates.templateCustomisations")}</h4>
                        <div>
                            <div className="template-customisations__field">
                                <label>{t("emailTemplates.merchantMessage")}</label>
                                <textarea value={bodyData.merchantMessage} onChange={(e)=>{setBodyData((prev)=>({...prev, merchantMessage: sanitizeInput(e.target.value, 'textarea')}))}} rows={5}>
                                    {bodyData.merchantMessage}
                                </textarea>
                            </div>
                            <div className="template-customisations__field terms-and-conditions-editor">
                                <label>Add Terms and Conditions</label>
                                <MarkdownEditor termsAndConditions={termsAndConditions} setTermsAndConditions={setTermsAndConditions} />
                            </div>
                            <div className="template-customisations__field">
                                <h4>Select a theme for your Gift Card</h4>
                                <div className="sample-templates-container">
                                    { model && (
                                        <PhysicalTemplateModalOpener
                                            setModel={setModel} 
                                            templateImages={templateImages}
                                            setTemplateImages={setTemplateImages}
                                        />
                                    )}
                                    <button className="load-templates-btn" onClick={() => setModel(true)}><ImportIcon /> {t("createGiftcard.loadTemplates")}</button>
                                </div>
                            </div>
                            {isPhysicalTemplate && physicalTemplates.length > 0 && (
                                <div className="template-customisations__field">
                                    <h4>Select a theme for your Gift Card</h4>
                                    <Splide
                                        options={{
                                            rewind: false,
                                            gap: "0.75rem",
                                            perPage: 4,
                                            pagination: false,
                                        }}
                                    >
                                        {physicalTemplates.map((imageUrl) => (
                                            <SplideSlide key={imageUrl}>
                                                <div className="thumbnail-image">
                                                    <img
                                                        src={imageUrl}
                                                        alt="Physical Template"
                                                        className={
                                                            selectedPhysicalTemplate === imageUrl
                                                                ? "active-main-image"
                                                                : ""
                                                        }
                                                        onClick={() => {
                                                            setSelectedPhysicalTemplate(imageUrl);
                                                            setBodyData((prev) => ({
                                                                ...prev,
                                                                templateBannerImage: imageUrl,
                                                            }));
                                                        }}
                                                    />
                                                </div>
                                            </SplideSlide>
                                        ))}
                                    </Splide>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}